import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';

const AVATAR_CACHE_KEY = 'velox:avatar';

const AccountContext = createContext(null);

/**
 * The signed-in user, fetched once for the whole app.
 *
 * Before this existed every surface that needed a name or a role called
 * GET /api/user/account for itself, which is why a single navigation used to
 * fire the same request three or four times. The shell, the account hub and
 * the nav all read from here instead.
 */
export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return ctx;
}

function readCachedAvatar(markerIso) {
  if (!markerIso) return null;
  try {
    const raw = sessionStorage.getItem(AVATAR_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // The marker is the cache key: a replaced picture changes it, so a stale
    // image can never survive an upload on another device.
    return parsed?.updatedAt === markerIso ? parsed.dataUrl || null : null;
  } catch {
    return null;
  }
}

function writeCachedAvatar(markerIso, dataUrl) {
  try {
    if (!markerIso || !dataUrl) {
      sessionStorage.removeItem(AVATAR_CACHE_KEY);
      return;
    }
    sessionStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify({ updatedAt: markerIso, dataUrl }));
  } catch {
    /* a full or unavailable sessionStorage only costs us the cache */
  }
}

function markerOf(account) {
  const value = account?.profileImage?.updatedAt;
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
}

export function AccountProvider({ children }) {
  const location = useLocation();

  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(localStorage.getItem('token')));
  const [error, setError] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);

  // Which token we last loaded for, so a sign-out/sign-in swap refetches and
  // an ordinary navigation does not.
  const loadedForTokenRef = useRef(null);
  const avatarForMarkerRef = useRef(null);

  const load = useCallback(async ({ force = false } = {}) => {
    const token = localStorage.getItem('token');

    if (!token) {
      loadedForTokenRef.current = null;
      avatarForMarkerRef.current = null;
      setAccount(null);
      setAvatarUrl(null);
      setError(null);
      setIsLoading(false);
      writeCachedAvatar(null, null);
      return null;
    }

    if (!force && loadedForTokenRef.current === token) return account;

    loadedForTokenRef.current = token;
    setIsLoading(true);

    try {
      const res = await api.get('/api/user/account');
      const next = res?.data || null;
      setAccount(next);
      setError(null);
      return next;
    } catch (e) {
      // A 401 is handled by the api interceptor (refresh, then redirect); only
      // surface something the account UI can act on.
      loadedForTokenRef.current = null;
      setError('Unable to load your account right now.');
      return null;
    } finally {
      setIsLoading(false);
    }
    // `account` is read only to return the memoised value; depending on it
    // would rebuild this callback on every load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // Re-checked on navigation so signing in (which routes to /home) picks the
    // new token up without the provider having to remount.
  }, [load, location.pathname]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const marker = markerOf(account);

  // Fetch the avatar bytes only when the marker says one exists and we do not
  // already hold that exact version.
  useEffect(() => {
    if (!marker) {
      avatarForMarkerRef.current = null;
      setAvatarUrl(null);
      writeCachedAvatar(null, null);
      return undefined;
    }

    if (avatarForMarkerRef.current === marker) return undefined;

    const cached = readCachedAvatar(marker);
    if (cached) {
      avatarForMarkerRef.current = marker;
      setAvatarUrl(cached);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/user/profile-picture');
        if (cancelled) return;
        const dataUrl = res?.data?.dataUrl || null;
        avatarForMarkerRef.current = marker;
        setAvatarUrl(dataUrl);
        writeCachedAvatar(marker, dataUrl);
      } catch {
        if (cancelled) return;
        // Initials are a complete fallback, so a failed image fetch is not an
        // error the user needs to see.
        setAvatarUrl(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [marker]);

  const refresh = useCallback(() => load({ force: true }), [load]);

  /** Merge a saved server response into the shared account without a refetch. */
  const mergeAccount = useCallback((patch) => {
    setAccount((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const uploadAvatar = useCallback(async (dataUrl) => {
    const res = await api.post('/api/user/profile-picture', { dataUrl });
    const nextUrl = res?.data?.dataUrl || null;
    const nextMarker = res?.data?.updatedAt ? new Date(res.data.updatedAt).toISOString() : null;

    avatarForMarkerRef.current = nextMarker;
    setAvatarUrl(nextUrl);
    writeCachedAvatar(nextMarker, nextUrl);
    setAccount((prev) =>
      prev ? { ...prev, profileImage: { ...(prev.profileImage || {}), updatedAt: res?.data?.updatedAt || null } } : prev
    );
    return nextUrl;
  }, []);

  const removeAvatar = useCallback(async () => {
    await api.delete('/api/user/profile-picture');
    avatarForMarkerRef.current = null;
    setAvatarUrl(null);
    writeCachedAvatar(null, null);
    setAccount((prev) => (prev ? { ...prev, profileImage: { updatedAt: null } } : prev));
  }, []);

  /** Called by sign-out so the next signed-in user never sees stale identity. */
  const clearAccount = useCallback(() => {
    loadedForTokenRef.current = null;
    avatarForMarkerRef.current = null;
    setAccount(null);
    setAvatarUrl(null);
    setError(null);
    setIsLoading(false);
    writeCachedAvatar(null, null);
  }, []);

  const value = useMemo(
    () => ({
      account,
      avatarUrl,
      isLoading,
      error,
      refresh,
      mergeAccount,
      uploadAvatar,
      removeAvatar,
      clearAccount,
    }),
    [account, avatarUrl, isLoading, error, refresh, mergeAccount, uploadAvatar, removeAvatar, clearAccount]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export default AccountContext;
